import type { WaitingExperiment } from './home-states';
import { useNavigate } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { ActionButton, CountBadge, Panel, SeverityDot } from '../ConsolePanel';
import { ScriptsModal } from '../Websites/List/ScriptsModal';
import { formatWait } from './home-states';

type WaitingForVisitorsPanelProps = {
  waiting: WaitingExperiment[];
  websiteId: number;
};

/**
 * Running experiments that have not recorded anyone yet.
 *
 * Without this, a just-started experiment showed "0 sessions" next to "Nothing needs you
 * right now", which is true for ten minutes and misleading for a day. The first hour is
 * normal and says so; after that the row turns amber, and after a day the attention card
 * raises it as no-data.
 */
export const WaitingForVisitorsPanel = ({ waiting, websiteId }: WaitingForVisitorsPanelProps) => {
  const navigate = useNavigate();
  const overdue = waiting.filter((entry) => entry.isOverdue).length;

  return (
    <Panel
      title="Waiting for first visitors"
      description="Running, but no session recorded yet."
      tourAnchor="home-waiting"
      bodyClassName="p-0"
      actions={overdue > 0 && <CountBadge severity="decide">{overdue} still empty after an hour</CountBadge>}
    >
      <div className="divide-line divide-y">
        {waiting.map(({ experiment, waitedMs, isOverdue }) => (
          <div
            key={experiment.id}
            className="flex flex-col gap-1.5 px-3.5 py-2.5 md:flex-row md:items-center md:gap-2.5"
          >
            <div className="flex min-w-0 items-center gap-2.5 md:max-w-[40%] md:shrink-0">
              <SeverityDot severity={isOverdue ? 'decide' : 'queue'} />
              <NavLink
                to={`/experiments/${experiment.id}`}
                title={experiment.name}
                className="min-w-0 truncate text-meta hover:underline"
              >
                {experiment.name}
              </NavLink>
            </div>
            <span className="supporting min-w-0 pl-4 md:flex-1 md:pl-0">
              {isOverdue
                ? `No visitors in the ${formatWait(waitedMs)} since it started. Something is likely stopping it from loading.`
                : `Started ${formatWait(waitedMs)} ago. First numbers usually appear within 10 minutes of the first visit.`}
            </span>
            <ActionButton
              className="ml-4 self-start md:ml-0 md:self-auto"
              onClick={() => navigate(`/experiments/${experiment.id}?screen=settings`)}
              title={`Check setup for ${experiment.name}`}
            >
              Check setup
              <ChevronRight className="size-3" strokeWidth={1.5} />
            </ActionButton>
          </div>
        ))}
      </div>

      <div className="border-line border-t px-3.5 py-3">
        <span className="micro-label">If nothing arrives</span>
        <ul className="supporting m-0 mt-2 flex list-disc flex-col gap-1 pl-5">
          <li>
            The tracking snippet is on the pages the experiment targets.{' '}
            <ScriptsModal
              websiteId={websiteId}
              renderTrigger={(open) => (
                <button type="button" onClick={open} className="text-accent hover:underline">
                  Show the snippet
                </button>
              )}
            />
          </li>
          <li>The experiment's URL targeting matches a page that gets visitors.</li>
          <li>
            Open the targeted page yourself in a private window. Your own visit is usually the first session to arrive.
          </li>
        </ul>
      </div>
    </Panel>
  );
};
